import { useCallback, useEffect, useMemo, useState } from 'react';

import Button from '@preqin/ui-components/cjs/core/Buttons/Button';
import Dropdown from '@preqin/ui-components/cjs/core/Dropdown';
import { Download } from '@preqin/ui-components/cjs/icons/file';
import { Save } from '@preqin/ui-components/cjs/icons/formatting';
import { Renew } from '@preqin/ui-components/cjs/icons/operations';

import { Report, ReportFilter } from '@/api/types';
import { ToastAlertProps } from '@/components/alert/types';
import { FilterSettings } from '@/types';

import { useFilter } from '../../../hooks/useFilter';
import { ReportFilterDeleteModal } from '../../modal/ReportFilterDeleteModal';
import { ReportFilterSaveModal } from '../../modal/ReportFilterSaveModal';
import { ReportFilterUpdateModal } from '../../modal/ReportFilterUpdateModal';
import { getSlicerStates } from '../PowerBiReportUtil';

import styles from './PowerBIReportFilterPanel.module.scss';

interface Props {
  report: Report;
  isFilterEnabled: boolean;
  isFileExportEnabled: boolean;
  setAlertMessage: (value: ToastAlertProps) => void;
  visualTitles: string[];
}

type FilterOption = {
  label: string;
  id: number;
};

export const PowerBIReportFilterPanel = ({
  report,
  isFilterEnabled,
  isFileExportEnabled,
  setAlertMessage,
  visualTitles,
}: Props) => {
  const [isModalOpen, setModalOpen] = useState<boolean>(false);
  const [isDeleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);
  const [filterSettings, setFilterSettings] = useState<FilterSettings[] | null>(null);
  const [selectedFilter, setSelectedFilter] = useState<string | null>(null); // this is filter name
  const { filters, isLoading, saveFilter, updateFilter, deleteFilters, setFilters } = useFilter({
    reportFilters: report?.filters,
    setModalOpen,
    setSelectedFilter,
    setDeleteModalOpen,
    setAlertMessage,
  });

  const saveButtonHandler = useCallback(async () => {
    //@ts-ignore
    const activePage = await window.report.getActivePage();
    const pageVisuals = await activePage.getVisuals();
    const visuals = pageVisuals.filter((visual) => visual.type === 'slicer' && visualTitles.includes(visual.title));
    const settings = await getSlicerStates(visuals);

    setFilterSettings(settings);
    setModalOpen(true);
  }, [visualTitles]);

  const saveSearchHandler = async (searchName: string) => {
    saveFilter({ settings: filterSettings, name: searchName, isDefaultFilter: false }, report.id);
  };

  const updateSearchHandler = () => {
    const editedFilter = filters.find((item: ReportFilter) => item.name === selectedFilter);
    updateFilter(
      { settings: filterSettings, name: editedFilter.name, isDefaultFilter: editedFilter.isDefaultFilter },
      report.id,
      editedFilter.id,
    );
  };

  const deleteSearchHandler = (ids) => {
    let idsToDelete = '';
    ids.forEach((id) => {
      idsToDelete += 'ids=' + id + '&';
    });
    deleteFilters(idsToDelete, report.id);
  };

  const clearFiltersHandler = async () => {
    //@ts-ignore
    const activePage = await window.report.getActivePage();
    const pageVisuals = await activePage.getVisuals();
    pageVisuals.map(async (visual) => {
      if (visualTitles.includes(visual.title) && visual.type === 'slicer') {
        await visual.setSlicerState({ filters: [] });
      }
    });
    setSelectedFilter(null);
    setFilterSettings(null);
  };

  const filterOptions: FilterOption[] = useMemo(
    () => filters.map((filter) => ({ label: filter.name, id: filter.id })),
    [filters],
  );

  const onSelectFilter = async (filter: FilterOption | null) => {
    if (filter.label === 'No value') {
      clearFiltersHandler();
    } else {
      setSelectedFilter(filter.label);
      //@ts-ignore
      const activePage = await window.report.getActivePage();
      const pageVisuals = await activePage.getVisuals();
      const originalFilter = filters.find((item: ReportFilter) => item.name === filter.label);

      if (originalFilter) {
        originalFilter.settings.forEach(async (setting) => {
          const visual = pageVisuals.find((visual) => visual.type === 'slicer' && visual.title === setting.title);
          if (visual) {
            await visual.setSlicerState({ filters: setting.filters });
          }
        });
      }
    }
  };

  const saveModalCloseHandler = () => {
    setSelectedFilter(null);
    setFilterSettings(null);
    setModalOpen(!isModalOpen);
  };

  // We need to reset everthing when selected report has changed
  useEffect(() => {
    setFilters(report.filters);
    setSelectedFilter(null);
    setFilterSettings(null);
  }, [report]);

  return (
    <div className={styles.filterPanel}>
      {isFilterEnabled && (
        <>
          <div style={{ width: 350 }}>
            <Dropdown
              label={!filterOptions || filterOptions.length === 0 ? 'No saved search' : 'Load search'}
              dropdownName="Select filter"
              type="secondary"
              dropdownDisabled={!filterOptions || filterOptions.length === 0}
              searchable={filters.length > 9}
              dropdownOptions={filterOptions.map((option) => ({ label: option.label, value: option.id }))}
              singleselectIndicator="tag"
              selectedValue={selectedFilter}
              onSelect={onSelectFilter}
              showFooterButtons={true}
              footerButtonPrimaryText="Manage saved search"
              hideFooterSecondaryButton
              fitContent
              onFooterPrimaryClick={() => setDeleteModalOpen(true)}
              hideHorizontalScroll
            />
          </div>
          <Button
            text="Save search"
            handleOnClick={saveButtonHandler}
            iconStart={<Save height={16} width={16} />}
            type={'secondary'}
          />
          <Button
            text="Clear all filters"
            handleOnClick={clearFiltersHandler}
            iconStart={<Renew height={16} width={16} />}
            type={'secondary'}
          />
          {selectedFilter === null ? (
            <ReportFilterSaveModal
              isOpen={isModalOpen}
              isLoading={isLoading}
              handleCancel={saveModalCloseHandler}
              handleSubmit={saveSearchHandler}
              filters={filters}
            />
          ) : (
            <ReportFilterUpdateModal
              isOpen={isModalOpen}
              isLoading={isLoading}
              handleCancel={saveModalCloseHandler}
              handleSubmit={updateSearchHandler}
            />
          )}
          <ReportFilterDeleteModal
            isOpen={isDeleteModalOpen}
            isLoading={isLoading}
            filters={filters}
            handleCancel={() => setDeleteModalOpen(false)}
            handleSubmit={(ids) => deleteSearchHandler(ids)}
          />
        </>
      )}
      {isFileExportEnabled && (
        <Button
          text="Export"
          isDisabled
          handleOnClick={() => alert('Export file')}
          iconStart={<Download height={16} width={16} />}
          type={'secondary'}
        />
      )}
    </div>
  );
};
